/**
 * UI-6c Task 6 — CsatConfigDrawer.
 *
 * Create/edit drawer for a `csat_configs` row, hosted inside the shared
 * `ResourceDrawer` (width="wide" — the questions editor needs the room).
 *
 * Backend contract (apps/api/src/routes/admin/csat/{create,update}.ts):
 *   - POST  /admin/csat/configs       body matches `CreateCsatConfigSchema`:
 *       { name, trigger, questions, isActive,
 *         appliesToQueueId?: string | null,
 *         appliesToChannel?: string | null }
 *   - PATCH /admin/csat/configs/:id   partial of the same
 *
 * Save ownership mirrors `SipTrunkDrawer` — props are `{ open, config,
 * onClose, onSaved }` (no `onSave`). The drawer owns the POST/PATCH, fires
 * `onSaved()` + `onClose()` on success, and surfaces errors via toast while
 * staying open.
 *
 * Remount per config: `ZodForm` mount-initialises from `defaultValues` and
 * never re-syncs, so we pass `key={config?.id ?? 'new'}` to the form to
 * fully remount it when a different config (or create-mode) opens.
 *
 * Phase 1 scope choices:
 *   - `appliesToQueueId` is a free-text UUID field, NOT a queue picker — the
 *     plan explicitly defers queue pre-fetch to a later phase (§Task 6).
 *   - `appliesToChannel` is a free-text optional string (max 64) — channels
 *     aren't enumerated centrally yet.
 */
import { useRef, useState, type JSX } from 'react'
import { z } from 'zod'
import { Controller, useFormContext } from 'react-hook-form'
import ResourceDrawer from '../ResourceDrawer.js'
import QuestionEditor, { type CsatQuestion } from './QuestionEditor.js'
import {
  ZodForm,
  FormText,
  FormDropdown,
  FormCheckbox,
} from '../../forms/index.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/**
 * Shape of a `csat_configs` row as returned by `GET /admin/csat/configs`.
 * Mirrors the columns DDL in spec §3.1.
 */
export interface CsatConfig {
  id: string
  name: string
  trigger: 'post_call' | 'post_conversation' | 'both'
  questions: CsatQuestion[]
  isActive: boolean
  appliesToQueueId: string | null
  appliesToChannel: string | null
  createdAt: string
  updatedAt: string
}

export interface CsatConfigDrawerProps {
  open: boolean
  /** The config being edited; `null` = create mode. */
  config: CsatConfig | null
  onClose: () => void
  /** Called after a successful save so the parent can refetch + close. */
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

const TRIGGER_OPTIONS: Array<{ label: string; value: CsatConfig['trigger'] }> = [
  { label: 'Post-Call', value: 'post_call' },
  { label: 'Post-Conversation', value: 'post_conversation' },
  { label: 'Both', value: 'both' },
]

// ---------------------------------------------------------------------------
// Zod schema — mirrors `CreateCsatConfigSchema` on the backend
// ---------------------------------------------------------------------------

const QuestionSchema = z.object({
  id: z.string().optional(),
  type: z.enum(['score', 'yes_no']),
  prompt: z
    .string()
    .trim()
    .min(1, 'Prompt is required')
    .max(500, 'Max 500 characters'),
  weight: z.number().min(0).max(10).optional(),
})

const CsatConfigSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(128, 'Max 128 characters'),
  trigger: z.enum(['post_call', 'post_conversation', 'both']),
  questions: z.array(QuestionSchema).min(1, 'Add at least one question'),
  isActive: z.boolean(),
  // Phase 1: queue is a free-text UUID; empty string ⇒ null on submit.
  // A blank value is allowed; non-blank must look like a UUID.
  appliesToQueueId: z
    .string()
    .trim()
    .refine(
      (v) =>
        v === '' ||
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
          v,
        ),
      'Must be a UUID',
    ),
  appliesToChannel: z.string().trim().max(64, 'Max 64 characters'),
})

type FormValues = z.input<typeof CsatConfigSchema>

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CsatConfigDrawer({
  open,
  config,
  onClose,
  onSaved,
}: CsatConfigDrawerProps): JSX.Element {
  const toast = useToast()
  const formRef = useRef<HTMLFormElement>(null)
  const [saving, setSaving] = useState(false)

  const isEdit = config !== null
  /** Remount key — flips when a different config (or create mode) opens. */
  const contentKey = config?.id ?? 'new'

  // --- Default values ------------------------------------------------------

  const defaultValues: FormValues = config
    ? {
        name: config.name,
        trigger: config.trigger,
        questions: config.questions.map((q) => ({
          ...(q.id !== undefined ? { id: q.id } : {}),
          type: q.type,
          prompt: q.prompt,
          ...(q.weight !== undefined ? { weight: q.weight } : {}),
        })),
        isActive: config.isActive,
        appliesToQueueId: config.appliesToQueueId ?? '',
        appliesToChannel: config.appliesToChannel ?? '',
      }
    : {
        name: '',
        trigger: 'post_call',
        // Seed with one blank question so the editor has at least 1 row and
        // the "cannot remove last" UX is engaged immediately.
        questions: [
          {
            id: crypto.randomUUID(),
            type: 'score',
            prompt: '',
            weight: 1,
          },
        ],
        isActive: true,
        appliesToQueueId: '',
        appliesToChannel: '',
      }

  // --- Save flow -----------------------------------------------------------

  async function handleSubmit(values: FormValues): Promise<void> {
    setSaving(true)
    try {
      // Marshal the form's empty-string scope fields back to `null` so the
      // backend receives the canonical "applies to everything" sentinel.
      const body = {
        name: values.name.trim(),
        trigger: values.trigger,
        questions: values.questions,
        isActive: values.isActive,
        appliesToQueueId:
          values.appliesToQueueId && values.appliesToQueueId.length > 0
            ? values.appliesToQueueId
            : null,
        appliesToChannel:
          values.appliesToChannel && values.appliesToChannel.length > 0
            ? values.appliesToChannel
            : null,
      }
      if (config) {
        await api.patch(`/admin/csat/configs/${config.id}`, body)
        toast.success('CSAT config updated')
      } else {
        await api.post('/admin/csat/configs', body)
        toast.success('CSAT config created')
      }
      onSaved()
      onClose()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to save CSAT config.',
      )
    } finally {
      setSaving(false)
    }
  }

  // --- Render --------------------------------------------------------------

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => {}}
      formRef={formRef}
      saving={saving}
      width="wide"
      title={isEdit ? `Edit CSAT Config: ${config?.name}` : 'New CSAT Config'}
    >
      <ZodForm
        key={contentKey}
        schema={CsatConfigSchema}
        defaultValues={defaultValues}
        onSubmit={(data) => handleSubmit(data as FormValues)}
        formRef={formRef}
      >
        <FormText
          name="name"
          label="Name"
          required
          autoFocus
          placeholder="Post-Call Standard"
          helpText="A human-readable label for this CSAT survey (1-128 characters)."
        />

        <FormDropdown
          name="trigger"
          label="Trigger"
          required
          options={[...TRIGGER_OPTIONS]}
          helpText="When the survey is dispatched."
        />

        <FormText
          name="appliesToQueueId"
          label="Applies to queue"
          placeholder="(all queues)"
          helpText="Optional UUID — leave blank to apply to all queues."
        />

        <FormText
          name="appliesToChannel"
          label="Applies to channel"
          placeholder="(all channels)"
          helpText="Optional channel name — leave blank to apply to all channels."
        />

        <FormCheckbox name="isActive" label="Active" />

        <QuestionsField />
      </ZodForm>
    </ResourceDrawer>
  )
}

// ---------------------------------------------------------------------------
// QuestionsField — Controller adapter so QuestionEditor participates in the
// form's validation flow.
// ---------------------------------------------------------------------------

/**
 * Adapts the controlled `QuestionEditor` to a react-hook-form field via
 * `Controller`. Errors are summarised inline; field-level errors per
 * question are surfaced by the toast summary inside `ZodForm`.
 */
function QuestionsField(): JSX.Element {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>()
  const error = errors.questions
  // RHF arrays produce either a single `message` (e.g. "Add at least one
  // question") or an array of nested errors. We surface whichever exists.
  const arrayMessage =
    typeof error?.message === 'string' ? error.message : undefined
  const nestedMessage = Array.isArray(error)
    ? (() => {
        for (const entry of error) {
          if (entry && typeof entry === 'object' && 'prompt' in entry) {
            const prompt = (entry as { prompt?: { message?: string } }).prompt
            if (prompt?.message) return prompt.message
          }
        }
        return undefined
      })()
    : undefined
  const errorMessage = arrayMessage ?? nestedMessage

  return (
    <div className="flex flex-col gap-1 mb-3">
      <span className="text-sm font-medium">
        Questions<span aria-hidden> *</span>
      </span>
      <Controller
        name="questions"
        control={control}
        render={({ field }) => (
          <QuestionEditor
            value={(field.value ?? []) as CsatQuestion[]}
            onChange={(next) => field.onChange(next)}
          />
        )}
      />
      {errorMessage && (
        <small role="alert" className="text-red-600">
          {errorMessage}
        </small>
      )}
    </div>
  )
}
